/**
 * React Query hooks for stock classes.
 * Replaces stock class logic from animalGroupTypes.ts.
 */

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { useGuardedMutation } from "model/useGuardedMutation";
import { useMemo } from "react";
import {
  getStockClassLU,
  LIVESTOCK_UNIT_CONVERSION,
  StockClass,
} from "./animalGroupTypes";
import { useCurrentFarmAnimalGroups } from "./useAnimalGroupQueries";

// Create API instance first, then export
const stockClassApi = new API<StockClass>("stockClasses");

// Re-export types and constants for convenience
export { getStockClassLU, LIVESTOCK_UNIT_CONVERSION, stockClassApi };
export type { StockClass };

/**
 * Fetch a single stock class by guid
 */
export function useStockClass(guid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.stockClasses.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("Stock class guid is required");
      }
      return await stockClassApi.getOne(guid);
    },
    enabled: enabled && !!guid,
  });
}

/**
 * Fetch multiple stock classes by their IDs
 */
export function useStockClassesByIds(
  stockClassIds: string[] | undefined,
  enabled = true
) {
  const queries = useQueries({
    queries: (stockClassIds || []).map((stockClassId) => ({
      queryKey: queryKeys.stockClasses.detail(stockClassId),
      queryFn: async () => {
        return await stockClassApi.getOne(stockClassId);
      },
      enabled:
        enabled && stockClassIds !== undefined && stockClassIds.length > 0,
    })),
  });

  // Memoize based on dataUpdatedAt to avoid creating new array references
  // when the underlying data hasn't changed (prevents infinite re-render loops)
  const dataKey = queries.map((q) => q.dataUpdatedAt).join(",");
  const stockClasses = useMemo(
    () =>
      queries
        .map((query) => query.data)
        .filter((sc): sc is StockClass => sc !== undefined),
    [dataKey]
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    data: stockClasses,
    isLoading,
    isError,
    queries,
  };
}

/**
 * Get stock classes for an animal group
 */
export function useStockClassesForAnimalGroup(
  animalGroupStockClassIds: string[] | undefined
) {
  return useStockClassesByIds(animalGroupStockClassIds);
}

/**
 * Get all stock classes for the current farm.
 * This is a convenience hook that combines useCurrentFarmAnimalGroups and useStockClassesByIds.
 * Replaces useStockClassesCompat which used cache reading without subscriptions.
 */
export function useCurrentFarmStockClasses() {
  const { data: animalGroups, isLoading: animalGroupsLoading } =
    useCurrentFarmAnimalGroups();

  // Collect all stock class IDs from all animal groups
  const stockClassIds = useMemo(() => {
    if (!animalGroups) return [];
    return animalGroups.flatMap((ag) => ag.stockClasses || []);
  }, [animalGroups]);

  const {
    data: stockClasses,
    isLoading: stockClassesLoading,
    isError,
    queries,
  } = useStockClassesByIds(stockClassIds, stockClassIds.length > 0);

  return useMemo(
    () => ({
      data: stockClasses,
      isLoading: animalGroupsLoading || stockClassesLoading,
      isError,
      queries,
    }),
    [stockClasses, animalGroupsLoading, stockClassesLoading, isError, queries]
  );
}

/**
 * Create a new stock class
 */
export function useCreateStockClass() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (stockClass: StockClass) => {
      const newStockClass = await stockClassApi.createOne(stockClass);
      return await stockClassApi.getOne(newStockClass.guid);
    },
    onSuccess: async (newStockClass) => {
      queryClient.setQueryData(
        queryKeys.stockClasses.detail(newStockClass.guid),
        newStockClass
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.stockClasses.all,
      });
      // Invalidate animal group to update its stockClasses array
      await queryClient.invalidateQueries({
        queryKey: queryKeys.animalGroups.detail(newStockClass.animalGroup),
      });
    },
  });
}

/**
 * Update an existing stock class
 */
export function useUpdateStockClass() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (stockClass: StockClass) => {
      const updatedStockClass = await stockClassApi.updateOne(stockClass);
      return await stockClassApi.getOne(updatedStockClass.guid);
    },
    onMutate: async (stockClass) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.stockClasses.detail(stockClass.guid),
      });

      const previousStockClass = queryClient.getQueryData<StockClass>(
        queryKeys.stockClasses.detail(stockClass.guid)
      );

      if (previousStockClass) {
        queryClient.setQueryData<StockClass>(
          queryKeys.stockClasses.detail(stockClass.guid),
          { ...previousStockClass, ...stockClass }
        );
      }

      return { previousStockClass };
    },
    onError: (_err, stockClass, context) => {
      if (context?.previousStockClass) {
        queryClient.setQueryData(
          queryKeys.stockClasses.detail(stockClass.guid),
          context.previousStockClass
        );
      }
    },
    onSuccess: async (updatedStockClass) => {
      queryClient.setQueryData(
        queryKeys.stockClasses.detail(updatedStockClass.guid),
        updatedStockClass
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.stockClasses.all,
      });
    },
  });
}

/**
 * Delete a stock class
 */
export function useDeleteStockClass() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (stockClassId: string) => {
      await stockClassApi.deleteOne(stockClassId);
      return stockClassId;
    },
    onSuccess: async (stockClassId) => {
      queryClient.removeQueries({
        queryKey: queryKeys.stockClasses.detail(stockClassId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.stockClasses.all,
      });
    },
  });
}

/**
 * Helper function to calculate demand from stock classes array
 */
export function getDemandFromArray(stockClasses: StockClass[]): number {
  return stockClasses.reduce(
    (prev, curr) => prev + curr.consumptionKgPerDay * curr.animalCount,
    0
  );
}

/**
 * Helper function to calculate group weight from stock classes array
 */
export function getGroupWeightFromArray(stockClasses: StockClass[]): number {
  return stockClasses.reduce(
    (prev, curr) => prev + curr.averageAnimalWeightKg * curr.animalCount,
    0
  );
}

/**
 * Helper function to calculate total animal count from stock classes array
 */
export function getTotalAnimalCountFromArray(
  stockClasses: StockClass[]
): number {
  return stockClasses.reduce((prev, curr) => prev + curr.animalCount, 0);
}

/**
 * Helper function to calculate total livestock units from stock classes array
 */
export function getTotalLivestockUnitsFromArray(
  stockClasses: StockClass[]
): number {
  return stockClasses.reduce((total, sc) => total + getStockClassLU(sc), 0);
}

/**
 * Helper function to group stock classes by animal group ID from array
 */
export function groupStockClassesByAnimalGroupId(
  stockClasses: StockClass[]
): Record<string, StockClass[]> {
  return stockClasses.reduce(
    (acc: Record<string, StockClass[]>, stockClass) => {
      if (!acc[stockClass.animalGroup]) {
        acc[stockClass.animalGroup] = [];
      }
      acc[stockClass.animalGroup].push(stockClass);
      return acc;
    },
    {}
  );
}

/**
 * Hook to get demand for stock classes
 */
export function useStockClassDemand(stockClassIds: string[] | undefined) {
  const { data: stockClasses = [] } = useStockClassesByIds(stockClassIds);

  return useMemo(() => {
    return getDemandFromArray(stockClasses);
  }, [stockClasses]);
}

/**
 * Hook to get total animal count for stock classes
 */
export function useStockClassTotalAnimalCount(
  stockClassIds: string[] | undefined
) {
  const { data: stockClasses = [] } = useStockClassesByIds(stockClassIds);

  return useMemo(() => {
    return getTotalAnimalCountFromArray(stockClasses);
  }, [stockClasses]);
}

/**
 * Hook to get total livestock units for stock classes
 */
export function useStockClassTotalLivestockUnits(
  stockClassIds: string[] | undefined
) {
  const { data: stockClasses = [] } = useStockClassesByIds(stockClassIds);

  return useMemo(() => {
    return getTotalLivestockUnitsFromArray(stockClasses);
  }, [stockClasses]);
}

/**
 * Helper function to reset stock classes query cache
 */
export function resetStockClassesQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: queryKeys.stockClasses.all });
}

/**
 * Hook wrapper for resetStockClassesQueryCache
 */
export function useResetStockClassesQueryCache() {
  const queryClient = useQueryClient();
  return () => resetStockClassesQueryCache(queryClient);
}

/**
 * Fetch multiple stock classes by their IDs and populate React Query cache.
 * Used for prefetching during initialization.
 * @param stockClassIds - Array of stock class IDs to fetch
 * @param queryClient - React Query client instance
 * @returns The fetched stock classes
 */
export async function fetchStockClassesByIdQuery(
  stockClassIds: string[],
  queryClient: ReturnType<typeof useQueryClient>
): Promise<StockClass[]> {
  if (!stockClassIds || stockClassIds.length === 0) {
    return [];
  }

  // Fetch all stock classes
  const stockClasses = await stockClassApi.getMany(stockClassIds);

  // Update React Query cache for each stock class
  stockClasses.forEach((stockClass) => {
    queryClient.setQueryData(
      queryKeys.stockClasses.detail(stockClass.guid),
      stockClass
    );
  });

  return stockClasses;
}
